import { create } from 'zustand';

export interface ComposeEmailOptions {
	/** Prefilled recipient. */
	to?: string;
	/** Prefilled subject. */
	subject?: string;
}

interface ComposeEmailBridgeState {
	// Registered by the email module's host (mounted at app root). `null` when no
	// host is mounted (e.g. an app that doesn't include the email module).
	open: ((options: ComposeEmailOptions) => void) | null;
	setOpen: (handler: ((options: ComposeEmailOptions) => void) | null) => void;
}

/**
 * Decouples the shared `ClickToEmail` component (libs/web-ui) from the in-app
 * email composer (modules/email). The email module registers an `open` handler
 * via its host component at app root; web-ui triggers it through this bridge
 * without importing the feature module (which module-isolation forbids).
 */
export const useComposeEmailBridge = create<ComposeEmailBridgeState>((set) => ({
	open: null,
	setOpen: (handler): void => {
		set({ open: handler });
	},
}));

/** True when an in-app composer host is mounted and can be opened. */
export function isInAppComposeAvailable(): boolean {
	return useComposeEmailBridge.getState().open != null;
}

/**
 * Opens the in-app composer if a host is mounted. Returns false when no host is
 * available, so callers can fall back to a `mailto:` link.
 */
export function openInAppCompose(options: ComposeEmailOptions): boolean {
	const { open } = useComposeEmailBridge.getState();
	if (open == null) {
		return false;
	}
	open(options);
	return true;
}
